import { useEffect, useRef, useState } from 'react';
import type { RecordingFrame, RecordingInput } from '@1qa/shared';
import { ArrowLeft, DeleteLeft, House, Keyboard } from '@/components/icons';
import { Button, Input } from '@/components/ui';
import { api, errorMessage, openRecordingScreen } from '@/lib/api';
import { gestureOf, toDevicePixels } from '@/lib/emulatorGesture';
import { readSse } from '@/lib/sse';

/**
 * The emulator's screen while a performance script is recorded on one of 1QA's emulators: frames come from the
 * Android runner; clicks become taps, drags swipes, and typing goes into the field that has the focus.
 */
export function EmulatorScreen({ recordingId, active, onProblem }: { recordingId: string; active: boolean; onProblem: (message: string) => void }) {
  const [frame, setFrame] = useState<string | null>(null);
  const [lost, setLost] = useState(false);
  const [text, setText] = useState('');
  const img = useRef<HTMLImageElement>(null);
  const down = useRef<{ x: number; y: number; at: number } | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    let retry: ReturnType<typeof setTimeout> | undefined;
    const connect = async () => {
      try {
        const res = await openRecordingScreen(recordingId, ctrl.signal);
        setLost(false);
        let ended = false;
        await readSse(
          res.body!,
          (msg) => {
            if (msg.event === 'end') ended = true;
            if (msg.event !== 'frame') return;
            try {
              setFrame((JSON.parse(msg.data) as RecordingFrame).png);
            } catch {
              /* a broken frame: the next one comes soon */
            }
          },
          ctrl.signal,
        );
        if (!ended && !ctrl.signal.aborted) retry = setTimeout(() => void connect(), 1000);
      } catch {
        if (ctrl.signal.aborted) return;
        setLost(true);
        retry = setTimeout(() => void connect(), 2000);
      }
    };
    void connect();
    return () => {
      ctrl.abort();
      clearTimeout(retry);
    };
  }, [recordingId]);

  const send = (input: RecordingInput) => {
    if (!active) return;
    void api.recordingInput(recordingId, input).catch((err) => onProblem(errorMessage(err)));
  };

  const point = (e: React.PointerEvent) => {
    const el = img.current;
    if (!el?.naturalWidth) return null;
    return toDevicePixels(e.clientX, e.clientY, el.getBoundingClientRect(), { w: el.naturalWidth, h: el.naturalHeight });
  };

  return (
    <div className="space-y-3">
      <div className="relative mx-auto w-full max-w-[22rem] overflow-hidden rounded-[1.75rem] bg-black p-2 shadow-lg">
        {frame ? (
          <img
            ref={img}
            src={`data:image/png;base64,${frame}`}
            alt="The emulator's screen"
            draggable={false}
            className={`block w-full touch-none rounded-[1.25rem] select-none ${active ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
            onPointerDown={(e) => {
              const p = point(e);
              if (!p) return;
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              down.current = { ...p, at: performance.now() };
            }}
            onPointerUp={(e) => {
              const start = down.current;
              down.current = null;
              const p = point(e);
              if (start && p) send(gestureOf(start, p, performance.now() - start.at));
            }}
            onPointerCancel={() => (down.current = null)}
          />
        ) : (
          <div className="flex aspect-[9/19.5] items-center justify-center rounded-[1.25rem] bg-neutral-900 px-6 text-center text-sm text-neutral-300">
            {lost ? 'Reconnecting to the emulator…' : 'Waiting for the emulator’s screen…'}
          </div>
        )}
      </div>
      <div className="mx-auto flex max-w-[22rem] items-center justify-center gap-2">
        <Button size="sm" variant="secondary" icon={<ArrowLeft className="size-3" />} disabled={!active} onClick={() => send({ kind: 'key', key: 'back' })}>
          Back
        </Button>
        <Button size="sm" variant="secondary" icon={<House className="size-3" />} disabled={!active} onClick={() => send({ kind: 'key', key: 'home' })}>
          Home
        </Button>
        <Button size="sm" variant="secondary" icon={<DeleteLeft className="size-3" />} disabled={!active} onClick={() => send({ kind: 'key', key: 'delete' })} aria-label="Delete one character">
          Delete
        </Button>
      </div>
      <form
        className="mx-auto flex max-w-[22rem] gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!text) return;
          send({ kind: 'text', text });
          setText('');
        }}
      >
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Tap a field, then type here" disabled={!active} aria-label="Type into the app" autoComplete="off" />
        <Button type="submit" size="sm" variant="secondary" icon={<Keyboard className="size-3" />} disabled={!active || !text}>
          Type
        </Button>
      </form>
    </div>
  );
}
